import { useMemo } from "react"
import { Table, Section, Column, FieldType, FieldValue, Icon, Button, PriceField } from "../../../../../features/ui"
import { useDataTypesHooks } from "../../../../../features/data-types"
import { TableName } from "../../../../../tables"
import { SalePaymentItemData } from "../../../services/sale-operation-service/types"
import { useSaleOperationHooks } from "../../../hooks/saleOperationHooks"

export type Props = {
    salePaymentItems: SalePaymentItemData[]
    createMode?: boolean
    disableCreate?: boolean
    createTooltip?: string | string[]
    readOnly?: boolean
    onCreate?: () => void
    onEditRow?: (index: string) => void
    onRemoveRow?: (index: string) => void
    onDownloadReceipt?: (index: string) => void
    onUploadReceipt?: (index: string) => void
    roundingMaxAmount?: number
    onChangeRoundingAmount?: (amount?: number) => void
}

export const SalePaymentItemsTable = (props: Props) => {
    const { salePaymentItems, createMode, disableCreate, createTooltip, readOnly, onCreate, onEditRow, onRemoveRow, onDownloadReceipt, onUploadReceipt, roundingMaxAmount, onChangeRoundingAmount } = props

    const { describePaymentItemName, describePaymentItemDetail } = useSaleOperationHooks()
    const { formatValue } = useDataTypesHooks()

    const findItem = (index?: FieldValue) => salePaymentItems.find(salePaymentItem => salePaymentItem.index === index)!

    const handleDownloadReceipt = (index: string) => () => onDownloadReceipt?.(index)

    const handleUploadReceipt = (index: string) => () => onUploadReceipt?.(index)

    const columns: Column[] = [
        {
            name: 'name',
            type: FieldType.TEXT,
            label: 'Medio de Pago'
        },
        {
            name: 'detail',
            type: FieldType.TEXT,
            label: 'Detalle'
        },
        {
            name: 'amount',
            type: FieldType.PRICE,
            label: 'Monto',
            // The rounding amount is edited inline, without opening the payment item modal
            render: onChangeRoundingAmount ? (value?: FieldValue) => {
                const salePaymentItem = findItem(value)
                return salePaymentItem.isRounding ? (
                    <PriceField
                        id="roundingAmount"
                        value={salePaymentItem.amount}
                        min={0.01}
                        max={roundingMaxAmount}
                        onChange={onChangeRoundingAmount}
                    />
                ) : formatValue(salePaymentItem.amount, FieldType.PRICE)
            } : undefined
        },
        {
            name: 'receipt',
            type: FieldType.TEXT,
            label: '',
            render: (value?: FieldValue) => {
                const salePaymentItem = findItem(value)
                if (salePaymentItem.isRounding || salePaymentItem.creditInFavor) {
                    return <></>
                } else if (createMode) {
                    return salePaymentItem.receiptFile ? <Icon icon="file-pdf" color="tertiary" /> : <></>
                } else if (!!salePaymentItem.receiptUrl) {
                    return (
                        <Button
                            icon="download"
                            kind="tertiary"
                            small
                            tooltipLines={['Descargar', 'Comprobante']}
                            onClick={handleDownloadReceipt(salePaymentItem.index)}
                        />
                    )
                } else if (!readOnly) {
                    return (
                        <Button
                            icon="upload"
                            small
                            tooltipLines={['Subir', 'Comprobante']}
                            onClick={handleUploadReceipt(salePaymentItem.index)}
                        />
                    )
                }
                return <></>
            }
        }
    ]

    const rows = useMemo(() => salePaymentItems.map(salePaymentItem => {
        const name = describePaymentItemName(salePaymentItem)
        const detail = describePaymentItemDetail(salePaymentItem)
        return {
            index: salePaymentItem.index,
            name,
            detail,
            amount: onChangeRoundingAmount ? salePaymentItem.index : salePaymentItem.amount,
            receipt: salePaymentItem.index
        }
    }), [salePaymentItems, onChangeRoundingAmount])

    return (
        <Section
            id='salePaymentItems'
            space={12}
        >
            <Table
                columns={columns}
                rows={rows}
                rowId="index"
                createIcon="plus"
                createTooltip={createTooltip}
                disableCreate={disableCreate}
                small
                readOnly={!createMode}
                onCreate={onCreate}
                onEdit={onEditRow}
                onRemove={onRemoveRow}
            />
        </Section>
    )
}
